import {useCurrentWorkspaceDetailQuery} from "../../../../../../hooks/queries";
import {
  useSelfCustomAvatarHistoryQuery
} from "../../../../../../services/selfCustomAvatar/useSelfCustomAvatarHistoryQuery";
import {ReactComponent as InfoSuccessIcon} from "../info_success.svg";
import {ReactComponent as InfoWarningIcon} from './info_warning.svg';
import {ReactComponent as ArrowTopIcon} from "./arrow_top.svg";
import {customDayjs} from "../../../../../../utils/date";
import useDialog from "../../../../../../hooks/useDialog";
import SubscriptionPlanDialog from "../../../../../../components/SubscriptionPlan/SubscriptionPlanDialog";
import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import {twMerge} from "tailwind-merge";
import useAuth from "../../../../../../hooks/useAuth";
import useURI from "../../../../../../hooks/useURI";

const PLAN_SLOT_POLICY = {
  PRO: 2,
  BASIC: 1,
  FREE: 0,
};

const extractPlanFamily = (planName = "") => {
  const upper = planName.toUpperCase();
  if (upper.startsWith("PRO")) return "PRO";
  if (upper.startsWith("BASIC")) return "BASIC";
  if (upper.startsWith("FREE")) return "FREE";
  return "UNKNOWN";
};

const getSlotCountByPlanName = (planName = "") => {
  const family = extractPlanFamily(planName);
  return PLAN_SLOT_POLICY[family] ?? 0;
};

function Step1SlotGuide({next, close}) {
  const {data: workspace} = useCurrentWorkspaceDetailQuery();
  const {user} = useAuth();
  const {uri} = useURI();
  const {i18n} = useTranslation();
  const isKorean = i18n.language.startsWith('ko')
  const dialog = useDialog();
  const [refetchInterval, setRefetchInterval] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastTargetId, setToastTargetId] = useState(null);

  const shownToastIds = useRef(new Set(
    JSON.parse(localStorage.getItem('shown_avatar_fail_toasts') || '[]')
  ));

  const prevHistoriesRef = useRef([]);

  const planName = workspace?.subscription?.plan?.name;
  const planFamily = extractPlanFamily(planName);
  const isPause = workspace?.subscription?.status === "PAUSE";
  const total = getSlotCountByPlanName(planName);

  const {
    data: histories = [],
    refetch,
  } = useSelfCustomAvatarHistoryQuery(workspace?.id, {
    refetchInterval
  });

  const filteredHistories = histories.filter((h) => h.status !== "FAIL");
  const used = filteredHistories.length;
  const available = Math.max(total - used, 0);

  const hasHistory = filteredHistories.length > 0;
  const hasProcessing = histories.some(h => h.status === 'PROCESSING');
  const isSlotFull = available === 0;

  const [isGuideOpen, setIsGuideOpen] = useState(true);
  const toggleGuide = () => setIsGuideOpen((prev) => !prev);

  useEffect(() => {
    if (workspace?.id) refetch()
  }, [workspace?.id, refetch]);

  useEffect(() => {
    setRefetchInterval(hasProcessing ? 3000 : false)
  }, [hasProcessing]);

  useEffect(() => {
    const newlyFailed = histories.filter(h => {
      const wasProcessing = prevHistoriesRef.current.find(p => p.id === h.id && p.status === 'PROCESSING');
      return wasProcessing && h.status === 'FAIL' && !shownToastIds.current.has(h.id);
    });

    if (newlyFailed.length > 0) {
      const target = newlyFailed[0];
      setToastTargetId(target.id);
      setToastVisible(true);
      shownToastIds.current.add(target.id);
      localStorage.setItem('shown_avatar_fail_toasts', JSON.stringify([...shownToastIds.current]));
    }

    prevHistoriesRef.current = histories;
  }, [histories]);

  useEffect(() => {
    const unseenFails = histories.filter(
      h => h.status === 'FAIL' && !shownToastIds.current.has(h.id)
    );
    if (unseenFails.length > 0) {
      const target = unseenFails[0];
      setToastTargetId(target.id);
      setToastVisible(true);
      shownToastIds.current.add(target.id);
      localStorage.setItem('shown_avatar_fail_toasts', JSON.stringify([...shownToastIds.current]));
    }
  }, [histories]);

  const dismissToast = () => setToastVisible(false);

  const showSubscriptionPlanSelector = () => {
    dialog.open(<SubscriptionPlanDialog expanded/>)
  }

  const onClickDeleteRequest = () => {
    const base = uri['bug-report']
    const params = new URLSearchParams({
      email_address: user.email,
      workspace_url: window.location.href,
      browser_info: window.navigator.userAgent,
    }).toString()

    window.open(`${base}?${params}`, '_blank')
  }

  const allowedPlans = ["BASIC", "PRO"]

  const ctaConfig = (() => {
    if (isPause) {
      return {
        text: "구독 재개하고 아바타 생성하기",
        onClick: showSubscriptionPlanSelector,
        disabled: false,
        type: "resume",
      };
    }
    if (!allowedPlans.includes(planFamily)) {
      return {
        text: "플랜을 업그레이드하고 아바타 생성하기",
        onClick: showSubscriptionPlanSelector,
        disabled: false,
        type: "upgrade",
      };
    }
    if (hasProcessing) {
      return {
        text: '새로운 아바타 생성하기',
        onClick: () => {
        },
        disabled: true,
        type: 'processing',
        showWarning: true,
      };
    }
    if (isSlotFull) {
      return {
        text: "슬롯을 추가하고 아바타 생성하기",
        onClick: () => {
          const url = isKorean
            ? 'https://aivatar.ai/contactus'
            : 'https://www.aivatar.ai/en/contactus';
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        disabled: false,
        showWarning: true,
        type: "noSlot",
      };
    }
    return {
      text: "새로운 아바타 생성하기",
      onClick: next,
      disabled: false,
      type: "normal",
    };
  })();

  const renderSlotLink = () => {
    if (ctaConfig.type === "upgrade" || ctaConfig.type === "resume") {
      return (
        <div
          className="text-body01 text-center justify-start text-gs01-200 text-xs font-medium underline">
          슬롯 추가하기
        </div>
      );
    }
    return (
      <a
        href={
          isKorean
            ? "https://aivatar.ai/contactus"
            : "https://www.aivatar.ai/en/contactus"
        }
        target="_blank"
        rel="noopener noreferrer"
        className="text-body01 text-blue-600 text-xs font-medium underline cursor-pointer"
      >
        슬롯 추가하기
      </a>
    );
  };

  return (
    <div
      className="relative px-5 py-6 bg-white rounded-[9.73px] inline-flex flex-col justify-start items-center overflow-hidden w-full max-w-[780px]">
      {/* Toast */}
      {toastVisible && (
        <div className="absolute top-[104px] left-1/2 -translate-x-1/2 z-50">
          <div className="px-5 py-3 bg-red-100 rounded-lg inline-flex flex-col justify-center items-start gap-2.5">
            <div className="self-stretch inline-flex justify-start items-center gap-6">
              <div className="flex justify-start items-center gap-2">
                <InfoWarningIcon className="w-6 h-6 text-red-500"/>
                <div className="justify-start text-body01 text-red-500 text-sm font-medium">
                  아바타 생성에 실패했습니다. 다시 시도해 주세요.
                </div>
              </div>
              <button
                onClick={dismissToast}
                className="w-6 h-6 text-body01 text-red-500"
                aria-label="토스트 닫기"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="w-full px-2.5 flex justify-between items-center mb-3">
        <h2 className="text-headline02 text-black">셀프 커스텀 아바타</h2>
        <button onClick={close} className="w-6 h-6">✕</button>
      </div>

      {/* Slot Info */}
      <div className="w-full px-2.5 flex flex-col gap-3.5">
        <div className="px-7 rounded-lg border border-grayscale-01-200 flex justify-between items-center">
          <div className="w-[478px] h-10 px-3.5 py-1 flex items-center">
            <SlotItem label="총 아바타 슬롯" value={total}/>
            <SlotItem label="사용 중" value={used}/>
            <SlotItem label="생성 가능" value={available}/>
          </div>
          {renderSlotLink()}
          <button
            onClick={onClickDeleteRequest}
            className="text-body01 text-gs01-800 text-xs font-normal"
          >
            삭제 요청하기
          </button>
        </div>

        {/* Guide */}
        <div className="flex flex-col gap-[5px]">
          <div
            role="button"
            tabIndex={0}
            onClick={toggleGuide}
            onKeyDown={(e) => ['Enter', ' '].includes(e.key) && toggleGuide()}
            className="h-10 pl-9 pr-5 py-[5px] bg-blue-50 rounded flex justify-between items-center cursor-pointer"
          >
            <span className="text-subtitle02 text-gs01-800 font-bold">
              💡 셀프 커스텀 아바타 생성 가이드
            </span>
            <span className={twMerge(!isGuideOpen ? 'rotate-180' : '', 'transition-transform')}>
              <ArrowTopIcon/>
            </span>
          </div>

          {isGuideOpen && (
            <div className="px-11 py-3.5 bg-gs01-50 rounded flex flex-col gap-[6px]">
              <GuideItem text="30초에서 1분 길이의 영상을 촬영하세요."/>
              <GuideItem text="아바타의 이름을 설정하고 영상을 업로드하세요."/>
              <GuideItem text="몇 분 이내로 나만의 아바타가 만들어집니다."/>
            </div>
          )}
        </div>

        <div
          className="h-9 px-7 py-2.5 mt-[-8px] bg-blue-50 rounded outline outline-1 outline-offset-[-1px] outline-blue-400 flex items-center gap-3.5">
          <span className="text-body01 text-gs01-900 text-sm font-normal">
            🔍 촬영 가이드를 먼저 확인하시면 더 좋은 결과를 얻을 수 있습니다.
          </span>
          <a
            href="https://aivatar.gitbook.io/user-guide/ko/product-lineup/self-custom-avatar/self-filming-guide"
            target="_blank"
            rel="noopener noreferrer"
            className="text-body01 text-blue-950 text-sm font-medium cursor-pointer"
          >
            상세 가이드 보기↗
          </a>
        </div>

        {/* History Table */}
        {hasHistory ? (
          <AvatarTable histories={filteredHistories}/>
        ) : (
          <div className="h-44 px-3.5 py-2.5 bg-gs01-20 rounded-lg flex flex-col justify-center items-center gap-[5px]">
            <span className="text-body01 text-xs text-gs01-900">아직 생성한 셀프 커스텀 아바타가 없습니다.</span>
            <span className="text-body01 text-xs text-gs01-900 font-medium">지금 바로 아바타를 만들어 보세요. 🙌</span>
          </div>
        )}

        {/* CTA */}
        <div className="w-full flex flex-col justify-center items-center gap-1.5 mt-2">
          <button
            type="button"
            onClick={ctaConfig.onClick}
            disabled={ctaConfig.disabled}
            className={`px-7 py-2.5 rounded-lg flex justify-center items-center gap-[5px] ${
              ctaConfig.disabled ? 'bg-gs01-100' : 'bg-bcblue-500'
            }`}
          >
            <span className="text-body01 text-white text-sm font-bold">{ctaConfig.text}</span>
          </button>

          {ctaConfig.showWarning && (
            <div className="text-body01 text-gs01-800 text-[10px] font-light">
              {ctaConfig.type === 'processing'
                ? '현재 아바타가 생성 중입니다. 잠시만 기다려 주세요.'
                : '슬롯이 가득 찼습니다. 슬롯을 추가해야 합니다.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SlotItem({label, value}) {
  return (
    <div className="flex-1 flex justify-center items-center gap-2.5 whitespace-nowrap">
      <div className="text-body01 text-grayscale-01-900 text-xs font-medium">
        {label}: {value}
      </div>
    </div>
  );
}

function GuideItem({text}) {
  return (
    <div className="flex items-center gap-1">
      <InfoSuccessIcon/>
      <span className="text-body01 text-gs01-900 text-sm font-normal">{text}</span>
    </div>
  );
}

function AvatarTable({histories}) {
  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[672px] w-full">
        {/* Header */}
        <div className="flex justify-center bg-gs01-50 text-body01 text-xs font-bold text-gs01-800 gap-16">
          <div className="w-28 p-2 flex justify-center items-center">아바타 이름</div>
          <div className="w-28 p-2 flex justify-center items-center">생성일</div>
          <div className="w-28 p-2 flex justify-center items-center">상태</div>
        </div>

        {/* Rows */}
        <div className="max-h-[220px] overflow-y-auto space-y-[1px]">
          {histories.map((h) => (
            <div
              key={h.id}
              className="flex justify-center bg-white border-b border-gs01-200 text-body01 text-xs text-gs01-600 gap-16"
            >
              <div className="w-28 px-2 py-3 flex justify-center items-center whitespace-nowrap">
                {h.name}
              </div>
              <div className="w-28 px-2 py-3 flex justify-center items-center whitespace-nowrap">
                {customDayjs(h.createdAt).format("YYYY/MM/DD")}
              </div>
              <div className="w-28 px-2 py-3 flex justify-center items-center whitespace-nowrap">
                <StatusTag status={h.status}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Cell({children}) {
  return (
    <div className="w-28 h-8 px-2 py-3 flex justify-center items-center">
      <div className="text-gs01-600 text-xs font-normal whitespace-nowrap">{children}</div>
    </div>
  );
}

function StatusTag({status}) {
  const statusMap = {
    COMPLETED: {bg: "bg-green-100", text: "text-green-600", label: "활성화됨"},
    EXPIRED: {bg: "bg-red-100", text: "text-red-500", label: "라이선스 만료"},
    PROCESSING: {bg: "bg-orange-100", text: "text-orange-600", label: "생성 중"},
  };

  const s = statusMap[status?.toUpperCase()] || statusMap.PROCESSING;

  return (
    <div className={`inline-flex justify-center items-center px-3 py-1 rounded-full gap-1 ${s.bg}`}>
      <span className={`text-xs font-medium ${s.text} whitespace-nowrap`}>{s.label}</span>
    </div>
  );
}

export default Step1SlotGuide;
